import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import ShoppingCart from '../components/marketplace/ShoppingCart';
import { api } from '../lib/api/client';
import { getToken } from '../lib/auth/session-manager';
import type { RootState } from '../store';

interface CartItem {
  id: string;
  marketplace_service_id: string;
  service_id: string; // Alias for marketplace_service_id for frontend compatibility
  service_name?: string;
  provider_id?: string;
  provider_name?: string;
  quantity: number;
  unit_price_cents: number;
  term_months: number;
  price?: number;
  status: 'ready' | 'pending' | 'confirmed' | 'unavailable' | 'expired' | 'saved_for_later';
  product_class?: 'A' | 'B' | 'C';
  added_catalog_instance_id?: string;
  service_address?: string;
  selected_address_id?: string;
  selected_address_label?: string;
  selected_address_full?: {
    street_address: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  perk_info?: string;
  selected_perk_id?: string;
  selected_perk_name?: string;
  service_perks?: any[];
  is_selected?: boolean;
  // Variant-related fields
  variant_selections?: Record<string, string>;
  service_configuration?: { additional_users?: number; [key: string]: any };
  variant_price?: number | null;
}

interface CartSummary {
  id?: string;
  items: CartItem[];
  ready_items?: CartItem[];
  pending_items?: CartItem[];
  saved_for_later_items?: CartItem[];
  total_items: number;
  total_value_cents: number;
  attribution_breakdown: Record<string, number>;
}

interface CartContextType {
  cart: CartSummary | null;
  loading: boolean;
  error: string | null;
  cartModalOpen: boolean;
  openCartModal: () => void;
  closeCartModal: () => void;
  refreshCart: () => Promise<void>;
  updateCartItem: (itemId: string, updates: any) => Promise<void>;
  removeCartItem: (itemId: string) => Promise<void>;
  saveForLater: (itemId: string) => Promise<void>;
  moveToCart: (itemId: string) => Promise<void>;
  toggleItemSelection: (itemId: string, selected: boolean) => Promise<void>;
}

const ensureArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const normalizeCartSummary = (cartData: any): CartSummary => {
  const readyItems = ensureArray<CartItem>(cartData?.ready_items);
  const pendingItems = ensureArray<CartItem>(cartData?.pending_items);
  const savedForLaterItems = ensureArray<CartItem>(cartData?.saved_for_later_items);
  const itemsFromApi = ensureArray<CartItem>(cartData?.items);
  const items =
    itemsFromApi.length > 0 ? itemsFromApi : [...readyItems, ...pendingItems, ...savedForLaterItems];

  return {
    ...cartData,
    items,
    ready_items: readyItems,
    pending_items: pendingItems,
    saved_for_later_items: savedForLaterItems,
    total_items: typeof cartData?.total_items === 'number' ? cartData.total_items : items.length,
    total_value_cents:
      typeof cartData?.total_value_cents === 'number' ? cartData.total_value_cents : 0,
    attribution_breakdown:
      cartData?.attribution_breakdown && typeof cartData.attribution_breakdown === 'object'
        ? cartData.attribution_breakdown
        : {},
  };
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const lastUserIdRef = useRef<string | null>(null);
  const loadingRef = useRef(false); // Prevent concurrent cart loads

  const refreshCart = useCallback(async () => {
    const token = getToken();

    // Only attempt to load cart if user is authenticated AND token is available
    if (!user || !token) {
      setCart(null);
      setLoading(false);
      loadingRef.current = false;
      return;
    }

    // Role-based context loading - only customers should load cart
    if (user.role !== 'customer') {
      setCart(null);
      setLoading(false);
      loadingRef.current = false;
      return;
    }

    // Prevent multiple simultaneous cart loads
    if (loading || loadingRef.current) {
      return;
    }

    try {
      setLoading(true);
      loadingRef.current = true;
      const cartData = await api.getCart();
      setCart(normalizeCartSummary(cartData));
    } catch (err) {
      console.error('[CartContext] Error loading cart:', err);
      setCart(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user]);

  const openCartModal = useCallback(() => {
    setCartModalOpen(true);
  }, []);

  const closeCartModal = useCallback(() => {
    setCartModalOpen(false);
  }, []);

  // Cart item management methods
  const updateCartItem = useCallback(
    async (itemId: string, updates: any) => {
      try {
        setError(null);
        // Use the correct API method for updating cart items
        if (updates.quantity !== undefined) {
          await api.updateCartItemQuantity(itemId, updates.quantity);
        } else {
          // For other updates, use the generic patch method with correct path
          await api.patch(`/api/customer/cart/items/${itemId}`, updates);
        }
        await refreshCart(); // Refresh cart after update
      } catch (error: any) {
        setError(error.message || 'Failed to update cart item');
        throw error;
      }
    },
    [refreshCart],
  );

  const removeCartItem = useCallback(
    async (itemId: string) => {
      try {
        setError(null);
        await api.removeCartItem(itemId);
        await refreshCart(); // Refresh cart after removal
      } catch (error: any) {
        setError(error.message || 'Failed to remove cart item');
        throw error;
      }
    },
    [refreshCart],
  );

  const saveForLater = useCallback(
    async (itemId: string) => {
      try {
        setError(null);
        await api.saveCartItemForLater(itemId);
        await refreshCart(); // Refresh cart after save
      } catch (error: any) {
        setError(error.message || 'Failed to save item for later');
        throw error;
      }
    },
    [refreshCart],
  );

  const moveToCart = useCallback(
    async (itemId: string) => {
      try {
        setError(null);
        await api.moveCartItemToCart(itemId);
        await refreshCart(); // Refresh cart after move
      } catch (error: any) {
        setError(error.message || 'Failed to move item to cart');
        throw error;
      }
    },
    [refreshCart],
  );

  const toggleItemSelection = useCallback(async (itemId: string, selected: boolean) => {
    // For now, this is handled client-side in the cart display
    // Could be implemented server-side if needed
    console.log(`Item ${itemId} selection toggled to ${selected}`);
  }, []);

  // Avoid SSR hydration mismatch from cart drawer/modal internals.
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Load cart when user logs in, clear when user logs out
  // Stagger loading to prevent simultaneous API calls with other contexts
  useEffect(() => {
    // Prevent duplicate loads for the same user
    if (user && user.id === lastUserIdRef.current) {
      return;
    }

    // Update the ref to track the current user
    lastUserIdRef.current = user?.id || null;

    // Stagger cart loading to avoid rate limiting with other contexts
    const timeoutId = setTimeout(() => {
      if (user) {
        refreshCart();
      } else {
        // User logged out — clear cart immediately
        setCart(null);
        setLoading(false);
      }
    }, 150); // Delay longer to stagger with AddressContext (which uses 50ms)

    return () => clearTimeout(timeoutId);
  }, [user?.id]); // Use user.id instead of user object to prevent unnecessary re-runs

  // Listen for cart update events (e.g., after adding item to cart)
  // NOTE: The 'cartUpdated' event should NOT be dispatched during logout.
  // During logout, the useEffect above handles cart clearing via user→null.
  useEffect(() => {
    const handleCartUpdate = () => {
      // Re-check token from storage (not from closure) to avoid stale reads
      const token = getToken();
      if (user && token && user.role === 'customer') {
        refreshCart();
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [user, refreshCart]);

  const value = useMemo(
    () => ({
      cart,
      loading,
      error,
      cartModalOpen,
      openCartModal,
      closeCartModal,
      refreshCart,
      updateCartItem,
      removeCartItem,
      saveForLater,
      moveToCart,
      toggleItemSelection,
    }),
    [
      cart,
      loading,
      error,
      cartModalOpen,
      openCartModal,
      closeCartModal,
      refreshCart,
      updateCartItem,
      removeCartItem,
      saveForLater,
      moveToCart,
      toggleItemSelection,
    ],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
      {isClient ? <ShoppingCart open={cartModalOpen} onClose={closeCartModal} /> : null}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
